import React, { useEffect, useState } from 'react';
import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { propTypes } from '../../util/types';
import { buildVenuePageSchema, extractFaqsFromBlocks } from '../../util/seoDiscoverySchema';
import SeoBreadcrumbs from '../../components/SeoDiscovery/SeoBreadcrumbs';
import SeoContentSections from '../../components/SeoDiscovery/SeoContentSections';
import SeoUpcomingEvents from '../../components/SeoDiscovery/SeoUpcomingEvents';
import SeoInternalLinks from '../../components/SeoDiscovery/SeoInternalLinks';
import SeoFaqSection from '../../components/SeoDiscovery/SeoFaqSection';
import SeoPartnerSlots from '../../components/SeoDiscovery/SeoPartnerSlots';
import SeoVenueContact from '../../components/SeoDiscovery/SeoVenueContact';
import SeoEditorialTrust from '../../components/SeoDiscovery/SeoEditorialTrust';
import useHeroImagePosition from '../../components/SeoDiscovery/useHeroImagePosition';
import SeoHeroImagePreload from '../../components/SeoDiscovery/SeoHeroImagePreload';
import css from '../../components/SeoDiscovery/SeoDiscovery.module.css';

const rootUrl = () =>
  typeof window !== 'undefined'
    ? window.location.origin
    : (process.env.REACT_APP_MARKETPLACE_ROOT_URL || '').replace(/\/$/, '');

const uniqueValues = values => [...new Set(values.filter(Boolean))];

const heroImageUrls = image => uniqueValues([image?.url, ...(image?.variants || [])]);

const absoluteUrl = url => {
  if (!url) {
    return null;
  }
  return url.startsWith('http') ? url : `${rootUrl()}${url.startsWith('/') ? '' : '/'}${url}`;
};

const venueMetaDescription = (venue, meta) => {
  const loc = [venue.city, venue.state].filter(Boolean).join(', ');
  const fallback = `Find ${venue.venueName} tickets, upcoming events, and venue tips${
    loc ? ` for ${loc}` : ''
  }. Find your seat and purchase tickets on PromoTix.`;
  const description = meta?.metaDescription || fallback;
  return /not the box office|box office/i.test(description) ? fallback : description;
};

const venueSocialSharing = (venue, title, description) => {
  const imageUrl = absoluteUrl(venue.image?.url);
  if (!imageUrl) {
    return { title, description };
  }
  const imageAlt = venue.image?.alt || `${venue.venueName} venue image`;
  return {
    title,
    description,
    images1200: [{ name: imageAlt, url: imageUrl, width: 1200, height: 630 }],
    images600: [{ name: imageAlt, url: imageUrl, width: 600, height: 314 }],
  };
};

const useHeroSlideshow = image => {
  const allImages = heroImageUrls(image);
  const [index, setIndex] = useState(0);
  // SSR (and first client render, for hydration parity) renders only the first frame:
  // it is the LCP element and extra frames in the first HTML compete with it for
  // bandwidth. The remaining frames mount after hydration for the slideshow.
  const [showAllFrames, setShowAllFrames] = useState(false);

  useEffect(() => {
    setIndex(0);
  }, [image?.url]);

  useEffect(() => {
    if (allImages.length > 1) {
      setShowAllFrames(true);
    }
  }, [allImages.length]);

  useEffect(() => {
    if (typeof window === 'undefined' || allImages.length < 2) {
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (prefersReducedMotion) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setIndex(i => (i + 1) % allImages.length);
    }, 4500);

    return () => {
      window.clearInterval(interval);
    };
  }, [allImages.length]);

  return { images: showAllFrames ? allImages : allImages.slice(0, 1), index };
};

export const SeoDiscoveryVenuePageComponent = props => {
  const { payload, fetchInProgress, fetchError, staticContext } = props;
  const { ref: heroImageRef, position: heroImagePosition } = useHeroImagePosition();
  const { images: heroImages, index: activeHeroImageIndex } = useHeroSlideshow(payload?.venue?.image);

  if (fetchError?.status === 404 && staticContext) {
    staticContext.notfound = true;
  }

  if (fetchInProgress && !payload) {
    return null;
  }

  if (!payload?.venue) {
    return null;
  }

  const { venue, venueContact, editorialTrust, meta, contentBlocks, internalLinks, upcomingEvents, breadcrumbs, page, relatedVenues } =
    payload;
  const faqs = extractFaqsFromBlocks(contentBlocks);
  const partnerBlock = (contentBlocks || []).find(b => b.blockType === 'partner_slot');
  const canonicalPath = page?.canonicalPath || `/event-discovery/venues/${venue.slug}/`;
  const noIndex = page?.indexState && page.indexState !== 'index';
  const loc = [venue.city, venue.state].filter(Boolean).join(', ');
  const pageTitle = meta?.metaTitle || `${venue.venueName} Tickets | PromoTix`;
  const pageDescription = venueMetaDescription(venue, meta);

  const schema = buildVenuePageSchema({
    venue,
    canonicalPath,
    rootUrl: rootUrl(),
    breadcrumbs,
    faqs,
    upcomingEvents,
    meta: { ...meta, metaTitle: pageTitle, metaDescription: pageDescription },
  });

  return (
    <Page
      title={pageTitle}
      description={pageDescription}
      socialSharing={venueSocialSharing(venue, pageTitle, pageDescription)}
      schema={schema}
      scrollingDisabled={false}
    >
      {noIndex ? (
        <HelmetNoIndex />
      ) : null}
      <SeoHeroImagePreload url={heroImages[0]} />
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <article className={css.root}>
          <SeoBreadcrumbs items={breadcrumbs} />
          <header className={css.hero}>
            {venue.image?.url ? (
              <div className={css.heroImageFrame} ref={heroImageRef}>
                {heroImages.map((imageUrl, imageIndex) => {
                  const isActive = imageIndex === activeHeroImageIndex;
                  const isFirst = imageIndex === 0;
                  return (
                    <img
                      key={imageUrl}
                      className={`${css.heroImage} ${isActive ? css.heroImageActive : ''}`}
                      src={imageUrl}
                      alt={isActive ? venue.image.alt || `${venue.venueName} venue` : ''}
                      aria-hidden={isActive ? undefined : 'true'}
                      loading={isFirst ? 'eager' : 'lazy'}
                      /* First frame is the LCP element (preloaded in head); later frames
                         mount after hydration and must not compete for bandwidth. */
                      fetchpriority={isFirst ? 'high' : 'low'}
                      style={{ objectPosition: `50% ${heroImagePosition}%` }}
                    />
                  );
                })}
              </div>
            ) : null}
            <div className={css.heroCopy}>
              <h1 className={css.h1}>{meta?.h1 || `${venue.venueName} Tickets and Event Schedule`}</h1>
              {loc ? <p className={css.location}>{loc}</p> : null}
            </div>
          </header>

          <SeoVenueContact contact={venueContact} />
          <SeoEditorialTrust trust={editorialTrust} />
          <SeoInternalLinks links={internalLinks} mode="guide" title="Plan your visit" />
          <SeoUpcomingEvents events={upcomingEvents} venueName={venue.venueName} />
          <SeoInternalLinks links={internalLinks} mode="performers" title="Performers at this venue" />
          <SeoInternalLinks
            links={internalLinks}
            relatedVenues={relatedVenues}
            mode="city"
            venueName={venue.venueName}
            cityName={venue.city}
          />
          <SeoPartnerSlots partnerBlock={partnerBlock} venue={venue} subpageSlug="places-to-stay" />
          <SeoContentSections blocks={contentBlocks} excludeHero />
          <SeoFaqSection blocks={contentBlocks} />
        </article>
      </LayoutSingleColumn>
    </Page>
  );
};

const HelmetNoIndex = () => (
  <Helmet>
    <meta name="robots" content="noindex, follow" />
  </Helmet>
);

SeoDiscoveryVenuePageComponent.propTypes = {
  payload: object,
  fetchInProgress: bool,
  fetchError: propTypes.error,
  staticContext: object,
};

const mapStateToProps = state => {
  const s = state.SeoDiscoveryVenuePage || {};
  return {
    payload: s.payload,
    fetchInProgress: s.fetchInProgress,
    fetchError: s.fetchError,
  };
};

const SeoDiscoveryVenuePage = compose(
  withRouter,
  connect(mapStateToProps)
)(SeoDiscoveryVenuePageComponent);

export default SeoDiscoveryVenuePage;
